import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Nav, Spinner } from 'react-bootstrap';
import type {
  SkinAnalysisValidationPromptConfig,
  SkinAnalysisValidationPromptKey,
  SkinAnalysisValidationPromptSource,
  SkinAnalysisValidationPromptTemplates,
} from '@/models/SkinAnalysisValidationCopyPrompt';

export interface ValidationCopyPromptSandbox {
  config: SkinAnalysisValidationPromptConfig | null;
  systemTemplates: SkinAnalysisValidationPromptTemplates | null;
  templates: SkinAnalysisValidationPromptTemplates | null;
  isLoading: boolean;
  isSaving: boolean;
  isResetting: boolean;
  isDirty: boolean;
  error: string | null;
  updateSystemTemplate: (key: SkinAnalysisValidationPromptKey, value: string) => void;
  updateTemplate: (key: SkinAnalysisValidationPromptKey, value: string) => void;
  resetSystemTemplateToCodeDefault: (key: SkinAnalysisValidationPromptKey) => void;
  resetTemplateToCodeDefault: (key: SkinAnalysisValidationPromptKey) => void;
  revertEdits: () => void;
  save: () => Promise<void>;
  resetToCodeDefaults: () => Promise<void>;
}

interface ValidationCopyPromptEditorProps {
  sandbox: ValidationCopyPromptSandbox;
}

const SOURCE_LABELS: Record<SkinAnalysisValidationPromptSource, string> = {
  code_default: 'CODE DEFAULT',
  redis: 'REDIS',
  legacy_v1: 'LEGACY V1',
};

const GROUP_LABELS: Record<'planner' | 'report' | 'routine_card', string> = {
  planner: 'Planner selection',
  report: 'Customer report copy',
  routine_card: 'Routine card copy',
};

const TEXT_ENCODER = new TextEncoder();
type PromptRole = 'system' | 'user';

export interface ClientPromotionBundle {
  catalogVersion: SkinAnalysisValidationPromptConfig['catalogVersion'];
  sourceConfigFingerprint: string;
  capturedAt: string;
  sourceUpdatedAt: string | null;
  systemTemplates: SkinAnalysisValidationPromptTemplates;
  templates: SkinAnalysisValidationPromptTemplates;
}

export function buildClientPromotionBundle(
  config: SkinAnalysisValidationPromptConfig,
  capturedAt = new Date(),
): ClientPromotionBundle {
  return {
    catalogVersion: config.catalogVersion,
    sourceConfigFingerprint: config.fingerprint,
    capturedAt: capturedAt.toISOString(),
    sourceUpdatedAt: config.updatedAt ?? null,
    systemTemplates: config.systemTemplates,
    templates: config.templates,
  };
}

function downloadClientPromotionBundle(config: SkinAnalysisValidationPromptConfig): void {
  const bundle = buildClientPromotionBundle(config);
  const url = URL.createObjectURL(new Blob([`${JSON.stringify(bundle, null, 2)}\n`], { type: 'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `skin-analysis-client-prompts-${config.fingerprint.slice(0, 12)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function ValidationCopyPromptEditor({ sandbox }: ValidationCopyPromptEditorProps) {
  const [activeKey, setActiveKey] = useState<SkinAnalysisValidationPromptKey>('planner_top_concerns');
  const [activeRole, setActiveRole] = useState<PromptRole>('user');
  const effectiveActiveKey = sandbox.config?.definitions.some((candidate) => candidate.key === activeKey)
    ? activeKey
    : (sandbox.config?.definitions[0]?.key ?? activeKey);
  const definition = sandbox.config?.definitions.find((candidate) => candidate.key === effectiveActiveKey) ?? null;
  const source =
    activeRole === 'system'
      ? (sandbox.config?.systemSources[effectiveActiveKey] ?? null)
      : (sandbox.config?.sources[effectiveActiveKey] ?? null);
  const currentValue =
    activeRole === 'system'
      ? (sandbox.systemTemplates?.[effectiveActiveKey] ?? '')
      : (sandbox.templates?.[effectiveActiveKey] ?? '');
  const savedValue =
    activeRole === 'system'
      ? (sandbox.config?.systemTemplates[effectiveActiveKey] ?? '')
      : (sandbox.config?.templates[effectiveActiveKey] ?? '');
  const currentBytes = useMemo(() => TEXT_ENCODER.encode(currentValue).length, [currentValue]);
  const hasOverflow = useMemo(
    () =>
      Boolean(
        sandbox.config &&
          sandbox.systemTemplates &&
          sandbox.templates &&
          sandbox.config.definitions.some(
            (candidate) =>
              TEXT_ENCODER.encode(sandbox.systemTemplates![candidate.key]).length > candidate.systemMaximumBytes ||
              TEXT_ENCODER.encode(sandbox.templates![candidate.key]).length > candidate.maximumBytes,
          ),
      ),
    [sandbox.config, sandbox.systemTemplates, sandbox.templates],
  );
  const maximumBytes = definition
    ? activeRole === 'system'
      ? definition.systemMaximumBytes
      : definition.maximumBytes
    : 0;
  const defaultValue = definition
    ? activeRole === 'system'
      ? definition.defaultSystemTemplate
      : definition.defaultTemplate
    : '';
  const isBusy = sandbox.isSaving || sandbox.isResetting;
  const isCurrentDirty = currentValue !== savedValue;
  const groups = sandbox.config
    ? (['planner', 'report', 'routine_card'] as const).map((group) => ({
        group,
        definitions: sandbox.config!.definitions.filter((candidate) => candidate.group === group),
      }))
    : [];
  const effectiveSystemPrompts =
    sandbox.config?.effectiveSystemPrompts?.filter(({ promptKeys }) => promptKeys.includes(effectiveActiveKey)) ?? [];

  return (
    <Card className="mb-3 validation-prompt-workbench">
      <Card.Header className="py-2 d-flex justify-content-between align-items-center">
        <span>Skin Analysis Prompt Workbench</span>
        <Badge bg="warning" text="dark">
          ADMIN PREVIEW ONLY
        </Badge>
      </Card.Header>
      <Card.Body className="py-2 small">
        <Alert variant="info" className="py-2">
          Saved Redis prompts affect Recommendation Lab previews only. Public Client remains on code defaults and
          requires a separate code PR after validation.
        </Alert>
        <div className="recommendation-prompt-contract mb-3">
          <strong>Editable guidance, fixed safety contract</strong>
          <span>
            System guidance is sent in the model system slot; user guidance is sent with the grounded request facts.
            Schemas, canonical identities, grounding validators, and fallback behavior remain code-owned and always take
            priority.
          </span>
          <span>
            Planner prompts may influence concern and active priority only. Routine requirements, category scope,
            mandatory avoids, Vertex candidates, and Formula ranking remain server-owned.
          </span>
        </div>
        {sandbox.error && (
          <Alert variant="danger" className="py-2">
            {sandbox.error}
          </Alert>
        )}
        {sandbox.isLoading && (
          <div className="text-muted">
            <Spinner size="sm" className="me-2" />
            Loading the stored prompts…
          </div>
        )}
        {sandbox.config && sandbox.systemTemplates && sandbox.templates && definition && (
          <>
            <div className="validation-prompt-meta mb-3">
              <Badge bg="secondary">{sandbox.config.catalogVersion}</Badge>
              <code title={sandbox.config.fingerprint}>config {sandbox.config.fingerprint.slice(0, 12)}</code>
              {sandbox.config.updatedAt && <span>Saved {new Date(sandbox.config.updatedAt).toLocaleString()}</span>}
            </div>
            <div className="validation-prompt-layout">
              <Nav variant="pills" className="recommendation-prompt-tabs validation-prompt-key-list">
                {groups.map(({ group, definitions }) => (
                  <div className="validation-prompt-key-group" key={group}>
                    <strong>{GROUP_LABELS[group]}</strong>
                    {definitions.map((candidate) => {
                      const modified =
                        sandbox.systemTemplates![candidate.key] !== sandbox.config!.systemTemplates[candidate.key] ||
                        sandbox.templates![candidate.key] !== sandbox.config!.templates[candidate.key];
                      const overflow =
                        TEXT_ENCODER.encode(sandbox.systemTemplates![candidate.key]).length >
                          candidate.systemMaximumBytes ||
                        TEXT_ENCODER.encode(sandbox.templates![candidate.key]).length > candidate.maximumBytes;
                      return (
                        <Nav.Item key={candidate.key}>
                          <Nav.Link
                            as="button"
                            type="button"
                            active={candidate.key === effectiveActiveKey}
                            onClick={() => setActiveKey(candidate.key)}
                          >
                            <span>{candidate.title}</span>
                            {modified && (
                              <Badge bg="warning" text="dark">
                                MODIFIED
                              </Badge>
                            )}
                            {overflow && <Badge bg="danger">OVER LIMIT</Badge>}
                          </Nav.Link>
                        </Nav.Item>
                      );
                    })}
                  </div>
                ))}
              </Nav>
              <div className="validation-prompt-editor-pane">
                <Nav variant="tabs" className="mb-3" aria-label="Prompt role">
                  <Nav.Item>
                    <Nav.Link
                      as="button"
                      type="button"
                      active={activeRole === 'system'}
                      onClick={() => setActiveRole('system')}
                    >
                      System guidance
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      as="button"
                      type="button"
                      active={activeRole === 'user'}
                      onClick={() => setActiveRole('user')}
                    >
                      User guidance
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                  <div>
                    <h6 className="mb-1">{definition.title}</h6>
                    <p className="text-muted mb-0">{definition.purpose}</p>
                  </div>
                  <Badge
                    bg={source === 'redis' ? 'warning' : source === 'legacy_v1' ? 'info' : 'secondary'}
                    text={source === 'redis' ? 'dark' : 'light'}
                  >
                    {source ? SOURCE_LABELS[source] : '—'}
                  </Badge>
                </div>
                <div className="recommendation-prompt-scope mb-2">
                  <strong>Affected response fields</strong>
                  <ul className="recommendation-prompt-field-list">
                    {definition.affectedFields.map((field) => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                  <strong>Immutable rules</strong>
                  <ul>
                    {definition.immutableRules.map((rule) => (
                      <li key={rule}>{rule}</li>
                    ))}
                  </ul>
                </div>
                <Form.Group className="mb-2">
                  <Form.Label className="visually-hidden" htmlFor="validation-prompt-textarea">
                    {definition.title} {activeRole} guidance
                  </Form.Label>
                  <Form.Control
                    id="validation-prompt-textarea"
                    as="textarea"
                    className="validation-copy-prompt-textarea"
                    spellCheck={false}
                    value={currentValue}
                    onChange={(event) =>
                      activeRole === 'system'
                        ? sandbox.updateSystemTemplate(effectiveActiveKey, event.target.value)
                        : sandbox.updateTemplate(effectiveActiveKey, event.target.value)
                    }
                  />
                  <div className={currentBytes > maximumBytes ? 'text-danger' : 'text-muted'}>
                    {currentBytes.toLocaleString()} / {maximumBytes.toLocaleString()} UTF-8 bytes
                  </div>
                </Form.Group>
                <details className="validation-prompt-default mb-3">
                  <summary>Compare {activeRole} code default</summary>
                  <pre>{defaultValue || '(No additional guidance)'}</pre>
                </details>
                <section className="validation-prompt-effective mb-3" aria-label="Effective system prompt">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <strong>Effective system prompt</strong>
                    <Badge bg="secondary">LAST SAVED</Badge>
                  </div>
                  <p className="text-muted mb-2">
                    Primary-generation previews use runtime placeholders. Provider-enforced structured response schemas
                    are configured separately; any OUTPUT_SCHEMA below is instruction text only.
                  </p>
                  {sandbox.isDirty && (
                    <Alert variant="warning" className="py-2">
                      Effective prompts below reflect the last saved snapshot. Save all prompts to refresh them.
                    </Alert>
                  )}
                  {effectiveSystemPrompts.map((preview) => (
                    <details key={preview.id} open={effectiveSystemPrompts.length === 1}>
                      <summary>{preview.title}</summary>
                      <pre>{preview.systemPrompt}</pre>
                    </details>
                  ))}
                </section>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Button
                    size="sm"
                    disabled={isBusy || !sandbox.isDirty || hasOverflow}
                    onClick={() => void sandbox.save().catch(() => undefined)}
                  >
                    {sandbox.isSaving ? 'Saving…' : 'Save all prompts'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={isBusy || !sandbox.isDirty}
                    onClick={() => sandbox.revertEdits()}
                  >
                    Discard all edits
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={isBusy || currentValue === defaultValue}
                    onClick={() =>
                      activeRole === 'system'
                        ? sandbox.resetSystemTemplateToCodeDefault(effectiveActiveKey)
                        : sandbox.resetTemplateToCodeDefault(effectiveActiveKey)
                    }
                  >
                    Reset this {activeRole} guidance locally
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-success"
                    disabled={isBusy || sandbox.isDirty}
                    onClick={() => downloadClientPromotionBundle(sandbox.config!)}
                  >
                    Download Client promotion bundle
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={isBusy}
                    onClick={() => {
                      if (!window.confirm('Restore all saved prompts to the current code defaults?')) {
                        return;
                      }
                      void sandbox.resetToCodeDefaults().catch(() => undefined);
                    }}
                  >
                    {sandbox.isResetting ? 'Restoring…' : 'Restore all code defaults'}
                  </Button>
                </div>
                {isCurrentDirty && (
                  <div className="text-warning-emphasis mt-2">Save before running the next preview.</div>
                )}
              </div>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
